/**
 * Pure resolution helpers for `PracticeSetupScreen`/`DummyPracticeScreen`
 * (#499, restructured #676/#609) — turn a saved, possibly-stale config (see
 * `prefs.ts`) into values safe to seed a setup screen's controlled inputs
 * with. Split out from the components so the "does this saved value still
 * make sense" logic is unit-testable without React.
 *
 * A saved config can go stale in ways the wire-level schemas can't catch
 * (they only validate SHAPE): the champion may no longer exist (content
 * update), or the saved loadout may reference an ability outside that
 * champion's CURRENT pool. Both degrade to the ordinary "no selection yet"
 * defaults — the same resilience contract every other pref uses.
 */

import { toAbilityId, type AbilityId, type ChampionId, type OpponentSeat } from "@pit/protocol";
import { CHAMPIONS, DEFAULT_CHAMPION_ID } from "../champions/catalog.js";
import { abilityPoolFor } from "../champions/abilityPool.js";
import { defaultLoadout } from "../champions/loadout.js";
import type { PracticeConfig } from "../prefs.js";

/** The champion-id/loadout shape shared by `PracticeConfig` and
 *  `DummyPracticeConfig` — both structurally satisfy this. */
export interface SavedChampionLoadout {
  readonly championId: string;
  readonly loadout: readonly string[] | null;
}

/** Resolve a saved config's `championId` against the LIVE catalog, or
 *  `DEFAULT_CHAMPION_ID` if absent/unknown. */
export function resolveSavedChampionId(saved: SavedChampionLoadout | null): ChampionId {
  if (saved === null) return DEFAULT_CHAMPION_ID;
  const known = CHAMPIONS.find((c) => c.id === saved.championId);
  return known === undefined ? DEFAULT_CHAMPION_ID : known.id;
}

/**
 * Resolve a saved config's `loadout` for `championId`: valid only if EVERY
 * saved id parses as an `AbilityId` AND belongs to `championId`'s CURRENT
 * pool — otherwise falls back to `defaultLoadout(championId)` (the same
 * "no customization" default every other picker uses).
 */
export function resolveSavedLoadout(
  saved: SavedChampionLoadout | null,
  championId: ChampionId,
): readonly AbilityId[] {
  const fallback = defaultLoadout(championId);
  if (saved === null) return fallback;
  if (saved.loadout === null) return fallback;
  // Only trust a saved loadout for the SAME champion it was saved against —
  // a champion switch (this fn's caller re-resolves the champion first)
  // could otherwise carry over an unrelated champion's ability ids.
  if (saved.championId !== championId) return fallback;
  const pool = new Set(abilityPoolFor(championId));
  const resolved: AbilityId[] = [];
  for (const raw of saved.loadout) {
    let id: AbilityId;
    try {
      id = toAbilityId(raw);
    } catch {
      return fallback;
    }
    if (!pool.has(id)) return fallback;
    resolved.push(id);
  }
  return resolved.length > 0 ? resolved : fallback;
}

/**
 * ISSUE-773: the practice team sizes the SERVER accepts (`ws.ts`:
 * `teamSize !== 1 && teamSize !== 3` → `badMessage`). Deliberately a closed
 * pair, not "any number" — 2v2 etc. would need a server change first.
 */
export type PracticeTeamSize = 1 | 3;
export const PRACTICE_BOT_COUNT_MIN = 1;
export const PRACTICE_BOT_COUNT_MAX = 6;

/** Every selectable practice team size, in the order the toggle renders. */
export const PRACTICE_TEAM_SIZES: readonly PracticeTeamSize[] = [1, 3];

/** Default when nothing is saved yet: 3v3, preserving #676's behavior for
 *  every existing user. */
export const DEFAULT_PRACTICE_TEAM_SIZE: PracticeTeamSize = 3;

/** Non-creator seats on the creator's OWN team — the creator occupies the
 *  remaining one, so 1v1 has none. */
export function teammateSeatCount(teamSize: PracticeTeamSize): number {
  return teamSize - 1;
}

/**
 * ISSUE-773: recover which team size a saved config was saved AT, so
 * re-mounting the screen re-selects that mode. The seat-plan arrays carry
 * this implicitly (and are already validated as a 1v1-or-3v3 pair by
 * `PRACTICE_CONFIG_SCHEMA`), so there is no separate persisted mode field to
 * fall out of sync with the seats.
 */
export function resolveSavedTeamSize(saved: PracticeConfig): PracticeTeamSize {
  if (saved === null) return DEFAULT_PRACTICE_TEAM_SIZE;
  return saved.teammateSeats.length === 0 ? 1 : 3;
}

export function resolveSavedPracticeBotCount(saved: PracticeConfig): number | null {
  return saved?.practiceBotCount ?? null;
}

/** #676: the default 3v3 teammate-seats plan — both open (human), today's
 *  ordinary "share the link" behavior. */
const DEFAULT_TEAMMATE_SEATS: readonly OpponentSeat[] = [{ kind: "human" }, { kind: "human" }];

/** #676: the default 3v3 opponent-seats plan — all three open (human). */
const DEFAULT_OPPONENT_SEATS: readonly OpponentSeat[] = [
  { kind: "human" },
  { kind: "human" },
  { kind: "human" },
];

/** Resolve a saved config's `teammateSeats` (0 or 2 entries, ISSUE-773), or
 *  the all-human default when nothing is saved yet. The caller resizes to
 *  the currently-selected team size. */
export function resolveSavedTeammateSeats(saved: PracticeConfig): readonly OpponentSeat[] {
  return saved === null ? DEFAULT_TEAMMATE_SEATS : saved.teammateSeats;
}

/** Resolve a saved config's `opponentSeats` (1 or 3 entries, ISSUE-773), or
 *  the all-human default when nothing is saved yet. The caller resizes to
 *  the currently-selected team size. */
export function resolveSavedOpponentSeats(saved: PracticeConfig): readonly OpponentSeat[] {
  return saved === null ? DEFAULT_OPPONENT_SEATS : saved.opponentSeats;
}
